"use client";

import { useMemo, useState } from "react";
import { Check, ExternalLink, X } from "lucide-react";
import {
  Box,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";

import type { OpenRouterModel } from "@/ai/model/openrouter-models";
import {
  averageCost,
  CACHE_HIT_RATE,
  costBenefit,
  formatPriceUsd,
  modelLabel,
  OUTPUT_TO_INPUT_RATIO,
} from "@/lib/ai-model-ui";
import { useAppColors } from "@/styles/colors";

type SortMode = "value" | "name" | "cheap" | "expensive" | "smart";

const price = (model: OpenRouterModel) => model.pricing?.prompt ?? null;
const formatContext = (value: number | null) => {
  if (value == null) return "dinâmico";
  if (value >= 1_000_000)
    return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  return `${Math.round(value / 1000)}k`;
};

export function ModelPickerModal({
  open,
  onClose,
  onSelect,
  models,
  value,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  models: OpenRouterModel[];
  value?: string;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("value");
  const { semantic } = useAppColors();
  const badgeColors = {
    cheap: { strong: semantic.successStrong, soft: semantic.success },
    smart: { strong: semantic.infoStrong, soft: semantic.info },
  } as const;
  const badges = useMemo(() => {
    const byPriceAsc = (a: OpenRouterModel, b: OpenRouterModel) =>
      price(a)! - price(b)!;
    const withIntelligence = models.filter((model) => model.intelligence);
    const byIntelligenceDesc = (a: OpenRouterModel, b: OpenRouterModel) =>
      b.intelligence!.intelligenceIndex - a.intelligence!.intelligenceIndex;
    const ids = (items: OpenRouterModel[]) =>
      new Set(items.slice(0, 10).map((model) => model.id));
    return {
      smart10: ids([...withIntelligence].sort(byIntelligenceDesc)),
      cheap10: ids(
        [...models].filter((model) => price(model) !== null).sort(byPriceAsc),
      ),
    };
  }, [models]);
  const filteredModels = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return models
      .filter(
        (model) =>
          !normalized ||
          `${model.name} ${model.id}`.toLowerCase().includes(normalized),
      )
      .sort((a, b) => {
        if (sort === "name") return a.name.localeCompare(b.name);
        if (sort === "cheap" || sort === "expensive")
          return (
            ((price(a) ?? (sort === "cheap" ? Infinity : -Infinity)) -
              (price(b) ?? (sort === "cheap" ? Infinity : -Infinity))) *
            (sort === "expensive" ? -1 : 1)
          );
        if (sort === "smart")
          return (
            (b.intelligence?.intelligenceIndex ?? -Infinity) -
            (a.intelligence?.intelligenceIndex ?? -Infinity)
          );
        const aValue = costBenefit(a);
        const bValue = costBenefit(b);
        if (aValue === bValue) return a.name.localeCompare(b.name);
        if (aValue === Infinity) return -1;
        if (bValue === Infinity) return 1;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
        return bValue - aValue;
      });
  }, [models, query, sort]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Outro modelo
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.secondary",
          }}
        >
          <X size={18} aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: "hsl(var(--muted) / 0.45)",
            border: "1px solid hsl(var(--border))",
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            Escolha o modelo de IA
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare <Box component="b">inteligência, custo e contexto</Box>.
            Modelos mais caros costumam ser mais capazes, mas preço e
            inteligência nem sempre andam juntos.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            Toda IA pode cometer erros — confirme dados financeiros importantes.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            mb: 2,
            p: 1,
            border: "1px solid hsl(var(--border))",
            borderRadius: 1.5,
            bgcolor: "hsl(var(--card))",
          }}
        >
          <TextField
            autoFocus
            fullWidth
            label="Buscar modelo"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
            <InputLabel id="model-sort-label">Ordenar por</InputLabel>
            <Select
              size="small"
              labelId="model-sort-label"
              label="Ordenar por"
              value={sort}
              onChange={(event) => setSort(event.target.value as SortMode)}
            >
              <MenuItem value="value">Custo-benefício</MenuItem>
              <MenuItem value="name">Nome</MenuItem>
              <MenuItem value="cheap">Mais barato</MenuItem>
              <MenuItem value="expensive">Mais caro</MenuItem>
              <MenuItem value="smart">Mais inteligente</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack spacing={0.75} sx={{ maxHeight: "65vh", overflowY: "auto" }}>
          {filteredModels.map((model) => {
            const itemBadges = [
              badges.smart10.has(model.id)
                ? { label: "Mais inteligente", tone: "smart" as const }
                : null,
              badges.cheap10.has(model.id)
                ? { label: "Mais barato", tone: "cheap" as const }
                : null,
            ].filter((badge) => Boolean(badge));
            const selected = value === model.id;
            const pricing = model.pricing;
            const avgCost = averageCost(model);
            const score = costBenefit(model);
            return (
              <Box
                key={model.id}
                sx={{
                  position: "relative",
                  border: "1px solid",
                  borderColor: selected ? "hsl(var(--primary))" : "divider",
                  borderRadius: 1.5,
                  bgcolor: selected ? "hsl(var(--primary) / 0.08)" : undefined,
                }}
              >
                <ButtonBase
                  focusRipple
                  aria-pressed={selected}
                  onClick={() => {
                    onSelect(model.id);
                    onClose();
                  }}
                  sx={{
                    display: "block",
                    width: "100%",
                    p: 1,
                    pr: 5,
                    textAlign: "left",
                    borderRadius: 1.5,
                    "&:hover": { bgcolor: "action.hover" },
                    "&:focus-visible": {
                      outline: "2px solid hsl(var(--primary))",
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Typography fontWeight={700} noWrap>
                      {modelLabel(model.id, models)}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {itemBadges.map((badge) => {
                        const colors = badgeColors[badge!.tone];
                        return (
                          <Chip
                            key={badge!.label}
                            size="small"
                            label={badge!.label}
                            sx={{
                              height: 22,
                              fontWeight: 600,
                              bgcolor: alpha(colors.soft, 0.15),
                              color: colors.strong,
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={3} sx={{ mt: 0.75 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Inteligência
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        lineHeight={1.1}
                      >
                        {model.intelligence?.intelligenceIndex ?? "—"}
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        pricing ? (
                          <Stack spacing={0.25}>
                            <Typography variant="caption">
                              Custo médio por 1M tokens
                            </Typography>
                            <Typography variant="caption">
                              entrada {formatPriceUsd(pricing.prompt)} ×{" "}
                              {Number(
                                (1 - CACHE_HIT_RATE) * 100,
                              ).toLocaleString("pt-BR")}
                              % ={" "}
                              {formatPriceUsd(
                                pricing.prompt * (1 - CACHE_HIT_RATE),
                              )}
                            </Typography>
                            <Typography variant="caption">
                              cache {formatPriceUsd(pricing.inputCacheRead)} ×{" "}
                              {Number(CACHE_HIT_RATE * 100).toLocaleString(
                                "pt-BR",
                              )}
                              % ={" "}
                              {formatPriceUsd(
                                pricing.inputCacheRead * CACHE_HIT_RATE,
                              )}
                            </Typography>
                            <Typography variant="caption">
                              saída {formatPriceUsd(pricing.completion)} ×{" "}
                              {Number(
                                OUTPUT_TO_INPUT_RATIO * 100,
                              ).toLocaleString("pt-BR")}
                              % ={" "}
                              {formatPriceUsd(
                                pricing.completion * OUTPUT_TO_INPUT_RATIO,
                              )}
                            </Typography>
                            <Typography variant="caption" fontWeight={700}>
                              = {formatPriceUsd(avgCost!)}
                            </Typography>
                          </Stack>
                        ) : (
                          "Preço indisponível"
                        )
                      }
                      slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Custo médio (1M)
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={800}
                          lineHeight={1.1}
                        >
                          {avgCost === null ? "—" : formatPriceUsd(avgCost)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    código {model.intelligence?.codingIndex ?? "—"} · agente{" "}
                    {model.intelligence?.agenticIndex ?? "—"} · contexto{" "}
                    {formatContext(model.contextLength)} · custo-benefício{" "}
                    {score === Infinity
                      ? "∞"
                      : score === null
                        ? "—"
                        : score.toFixed(1)}
                  </Typography>
                  <Tooltip
                    title={model.description || "Sem descrição"}
                    slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {model.description || "Sem descrição"}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.25,
                      fontFamily: "monospace",
                      fontSize: 11,
                      opacity: 0.75,
                    }}
                  >
                    {model.id}
                  </Typography>
                </ButtonBase>
                {selected ? (
                  <Check
                    size={18}
                    aria-label="Modelo selecionado"
                    style={{ position: "absolute", right: 36, top: 12 }}
                  />
                ) : null}
                <IconButton
                  component="a"
                  href={`https://openrouter.ai/${model.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  aria-label="Ver no OpenRouter"
                  sx={{ position: "absolute", right: 8, top: 8, p: 0.25 }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <ExternalLink size={14} />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
